/**
 * The filter box and the pager of a list of points read a page at a time (lib/iot/instrument-points): an
 * instrument's points in the gateway drawer (InstrumentValues) and a gateway's own tags in its detail sheet. Pure.
 */
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { POINTS_PAGE_SIZE, pointsPageLabel, type PointsNoun } from "@/lib/iot/instrument-points";

const POINT: PointsNoun = { one: "point", many: "points" };

/** The name filter, shown above a list that runs past one page (or while a filter is typed). */
export function PointsFilter({
  value,
  onChange,
  noun = POINT,
}: {
  value: string;
  onChange: (text: string) => void;
  noun?: PointsNoun;
}) {
  return (
    <div className="relative">
      <Search aria-hidden className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Filter ${noun.many} by name`}
        aria-label={`Filter ${noun.many}`}
        className="h-7 pl-7 text-xs"
      />
    </div>
  );
}

/** Which points the page shows of how many, with Previous and Next when they run past one page. */
export function PointsPager({
  offset,
  shown,
  total,
  onPage,
  noun = POINT,
}: {
  offset: number;
  shown: number;
  total: number;
  onPage: (offset: number) => void;
  noun?: PointsNoun;
}) {
  return (
    <div className="flex items-center justify-between gap-2 pt-0.5 text-[11px] text-on-surface-muted">
      <span aria-live="polite" className="tabular-nums">
        {pointsPageLabel(offset, shown, total, noun)}
      </span>
      {total > POINTS_PAGE_SIZE && (
        <span className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-[11px]"
            disabled={offset <= 0}
            onClick={() => onPage(Math.max(0, offset - POINTS_PAGE_SIZE))}
          >
            <ChevronLeft aria-hidden className="size-3.5" />
            Previous
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-1.5 text-[11px]"
            disabled={offset + shown >= total}
            onClick={() => onPage(offset + POINTS_PAGE_SIZE)}
          >
            Next
            <ChevronRight aria-hidden className="size-3.5" />
          </Button>
        </span>
      )}
    </div>
  );
}
